import type { ApiMetafield, ApiMetaobject, FieldDefinition, MetafieldOwner } from '@dunea/plugin-sdk';
import httpClient from '~/lib/http-client';

// Owner-facing custom data API (studio `DataOwnerController`,
// apps/studio/src/modules/plugins/data). `appId` is the CHAT id. The owner
// manages `custom`; plugin namespaces (app--<slug>, shop…) are editable only
// where the plugin left `ownerWritable` on.

export interface DataDefinition {
  id: string;
  kind: 'metafield' | 'metaobject';
  ownerType: MetafieldOwner | 'metaobject';
  namespace: string;
  key: string;
  name: string;
  description: string | null;
  field?: FieldDefinition;
  fields?: FieldDefinition[];
  displayField?: string;
  access: { storefront: 'public' | 'none'; ownerWritable: boolean };
  pinned: boolean;
  ownerPlugin: string | null;
  entryCount?: number;
  updatedAt: string;
}

export interface MetaobjectPage {
  type: string;
  namespace: string;
  items: ApiMetaobject[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ResourceFields {
  ownerType: MetafieldOwner;
  ownerId: string;
  /** Pinned definitions for this resource type. */
  definitions: DataDefinition[];
  values: ApiMetafield[];
}

export interface MetaobjectTypeInput {
  type: string;
  name: string;
  description?: string;
  fields: FieldDefinition[];
  displayField?: string;
  access?: { storefront?: 'public' | 'none' };
}

export interface MetafieldDefinitionInput {
  ownerType: MetafieldOwner;
  key: string;
  name: string;
  description?: string;
  field: Omit<FieldDefinition, 'key' | 'label'>;
  access?: { storefront?: 'public' | 'none' };
  pinned?: boolean;
}

export interface BulkResult {
  created: number;
  updated: number;
  items: ApiMetaobject[];
  errors: { index: number; handle?: string; message: string; errors?: Record<string, string> }[];
}

const base = (appId: string) => `/api/plugins/data/apps/${encodeURIComponent(appId)}`;

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') {
      search.set(k, String(v));
    }
  }

  const s = search.toString();

  return s ? `?${s}` : '';
}

export async function apiListDataDefinitions(
  appId: string,
  filter: { kind?: 'metafield' | 'metaobject'; ownerType?: string; namespace?: string } = {},
): Promise<DataDefinition[]> {
  const { data } = await httpClient.get<DataDefinition[]>(`${base(appId)}/definitions${qs(filter)}`);
  return data;
}

export async function apiDefineMetaobject(appId: string, input: MetaobjectTypeInput): Promise<DataDefinition> {
  const { data } = await httpClient.post<DataDefinition>(`${base(appId)}/definitions/metaobjects`, input);
  return data;
}

export async function apiDefineMetafield(appId: string, input: MetafieldDefinitionInput): Promise<DataDefinition> {
  const { data } = await httpClient.post<DataDefinition>(`${base(appId)}/definitions/metafields`, input);
  return data;
}

/** Deletes the definition and its data (values, or every entry of the type). */
export async function apiDeleteDataDefinition(appId: string, definitionId: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/definitions/${encodeURIComponent(definitionId)}`);
}

export async function apiResourceFields(
  appId: string,
  ownerType: MetafieldOwner,
  ownerId: string,
): Promise<ResourceFields> {
  const { data } = await httpClient.get<ResourceFields>(`${base(appId)}/resource-fields${qs({ ownerType, ownerId })}`);
  return data;
}

/** A null value removes the metafield (answers `{ deleted: true }`). */
export async function apiSetMetafield(
  appId: string,
  input: { ownerType: MetafieldOwner; ownerId: string; namespace?: string; key: string; value: unknown },
): Promise<ApiMetafield | { deleted: true }> {
  const { data } = await httpClient.put<ApiMetafield | { deleted: true }>(`${base(appId)}/metafields`, input);
  return data;
}

export async function apiListMetaobjects(
  appId: string,
  type: string,
  opts: { namespace?: string; status?: 'draft' | 'active'; page?: number; limit?: number } = {},
): Promise<MetaobjectPage> {
  const { data } = await httpClient.get<MetaobjectPage>(`${base(appId)}/metaobjects${qs({ type, ...opts })}`);
  return data;
}

export async function apiCreateMetaobject(
  appId: string,
  input: {
    type: string;
    namespace?: string;
    handle?: string;
    fields: Record<string, unknown>;
    status?: 'draft' | 'active';
  },
): Promise<ApiMetaobject> {
  const { data } = await httpClient.post<ApiMetaobject>(`${base(appId)}/metaobjects`, input);
  return data;
}

export async function apiUpdateMetaobject(
  appId: string,
  id: string,
  patch: { fields?: Record<string, unknown>; handle?: string; status?: 'draft' | 'active' },
): Promise<ApiMetaobject> {
  const { data } = await httpClient.patch<ApiMetaobject>(`${base(appId)}/metaobjects/${encodeURIComponent(id)}`, patch);
  return data;
}

export async function apiDeleteMetaobject(appId: string, id: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/metaobjects/${encodeURIComponent(id)}`);
}

export async function apiReorderMetaobjects(
  appId: string,
  input: { type: string; namespace?: string; ids: string[] },
): Promise<MetaobjectPage> {
  const { data } = await httpClient.post<MetaobjectPage>(`${base(appId)}/metaobjects/reorder`, input);
  return data;
}
